import React from 'react';
import { Link } from 'react-router-dom';

const CookiePolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden py-24">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23EF5000%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
        
        <div className="relative px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6 text-5xl font-bold leading-tight text-white sm:text-6xl">
              Cookie <span className="bg-gradient-to-r from-primary-400 to-orange-400 bg-clip-text text-transparent">Policy</span>
            </h1>
            <p className="max-w-2xl mx-auto mb-10 text-xl text-gray-300 leading-relaxed">
              Learn how we use cookies and similar technologies to improve your experience.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative py-24 bg-gray-800/30 backdrop-blur-sm">
        <div className="px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
          <div className="max-w-4xl mx-auto">
            
            {/* Last Updated */}
            <div className="mb-12 text-center">
              <p className="text-gray-400 text-sm">
                Last updated: October 3, 2025
              </p>
            </div>

            <div className="space-y-12">
              
              {/* Introduction */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">What Are Cookies?</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    Cookies are small text files that are stored on your device when you visit our website. 
                    They help us provide you with a better experience by remembering your preferences and 
                    understanding how you use our Service.
                  </p>
                  <p>
                    This Cookie Policy explains how RF Digital ("we," "our," or "us") uses cookies and similar 
                    technologies on the Eddystone URL Shortener service (the "Service"), in compliance with 
                    South African privacy laws and international best practices.
                  </p>
                </div>
              </section>

              {/* Types of Cookies */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Types of Cookies We Use</h2>
                <div className="text-gray-300 space-y-6">
                  
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Essential Cookies</h3>
                    <p className="mb-2">These cookies are necessary for the Service to function properly:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Authentication cookies to keep you logged in</li>
                      <li>Security cookies to protect against fraud</li>
                      <li>Session cookies to maintain your preferences</li>
                      <li>Load balancing cookies for optimal performance</li>
                    </ul>
                    <p className="text-sm text-gray-400 mt-2">
                      <strong>Legal basis:</strong> These cookies are essential for providing the Service you requested.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Analytics Cookies</h3>
                    <p className="mb-2">These cookies help us understand how you use our Service:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Google Analytics for website usage statistics</li>
                      <li>Performance monitoring cookies</li>
                      <li>Error tracking and debugging cookies</li>
                      <li>User behavior analysis cookies</li>
                    </ul>
                    <p className="text-sm text-gray-400 mt-2">
                      <strong>Legal basis:</strong> Legitimate interest in improving our Service.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Functional Cookies</h3>
                    <p className="mb-2">These cookies enhance your experience:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Language and region preferences</li>
                      <li>Theme and display settings</li>
                      <li>Recently used features</li>
                      <li>Customization preferences</li>
                    </ul>
                    <p className="text-sm text-gray-400 mt-2">
                      <strong>Legal basis:</strong> Your consent and legitimate interest in providing personalized features.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Marketing Cookies</h3>
                    <p className="mb-2">These cookies help us show you relevant content:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Advertising preference cookies</li>
                      <li>Social media integration cookies</li>
                      <li>Campaign tracking cookies</li>
                      <li>Retargeting cookies</li>
                    </ul>
                    <p className="text-sm text-gray-400 mt-2">
                      <strong>Legal basis:</strong> Your explicit consent.
                    </p>
                  </div>
                </div>
              </section>

              {/* Third-Party Cookies */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Third-Party Cookies</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    We may use third-party services that set their own cookies. These include:
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="bg-gray-900/50 border border-gray-600 rounded-macos p-4">
                      <h4 className="font-semibold text-white mb-2">Google Analytics</h4>
                      <p className="text-sm">Helps us understand website usage and improve performance.</p>
                    </div>
                    <div className="bg-gray-900/50 border border-gray-600 rounded-macos p-4">
                      <h4 className="font-semibold text-white mb-2">Payment Processors</h4>
                      <p className="text-sm">Secure payment processing for subscription services.</p>
                    </div>
                    <div className="bg-gray-900/50 border border-gray-600 rounded-macos p-4">
                      <h4 className="font-semibold text-white mb-2">Firebase</h4>
                      <p className="text-sm">Authentication and database services for account management.</p>
                    </div>
                    <div className="bg-gray-900/50 border border-gray-600 rounded-macos p-4">
                      <h4 className="font-semibold text-white mb-2">CDN Services</h4>
                      <p className="text-sm">Content delivery for faster loading times.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Managing Cookies */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Managing Your Cookie Preferences</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    You have several options for managing cookies:
                  </p>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Browser Settings</h3>
                    <p className="mb-2">Most browsers allow you to:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>View and delete cookies</li>
                      <li>Block cookies from specific websites</li>
                      <li>Block third-party cookies</li>
                      <li>Clear all cookies when you close the browser</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Cookie Consent Manager</h3>
                    <p>
                      When you first visit our website, you'll see a cookie consent banner where you can:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Accept all cookies</li>
                      <li>Reject non-essential cookies</li>
                      <li>Customize your preferences by cookie type</li>
                      <li>Change your preferences at any time</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Opt-Out Links</h3>
                    <p className="mb-2">You can opt out of specific third-party cookies:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li><a href="https://tools.google.com/dlpage/gaoptout" className="text-primary-400 hover:text-primary-300" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out</a></li>
                      <li><a href="https://www.youronlinechoices.com/" className="text-primary-400 hover:text-primary-300" target="_blank" rel="noopener noreferrer">Your Online Choices (EU)</a></li>
                      <li><a href="https://optout.networkadvertising.org/" className="text-primary-400 hover:text-primary-300" target="_blank" rel="noopener noreferrer">Network Advertising Initiative</a></li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Impact of Disabling */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Impact of Disabling Cookies</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    Disabling certain cookies may affect your experience with our Service:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>You may need to log in more frequently</li>
                    <li>Your preferences may not be saved</li>
                    <li>Some features may not work properly</li>
                    <li>We may not be able to provide personalized content</li>
                    <li>Analytics and performance improvements may be limited</li>
                  </ul>
                  <p>
                    Essential cookies cannot be disabled as they are necessary for the Service to function.
                  </p>
                </div>
              </section>

              {/* Data Retention */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Cookie Data Retention</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    Different cookies have different lifespans:
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <h4 className="font-semibold text-white mb-2">Session Cookies</h4>
                      <p className="text-sm">Deleted when you close your browser</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Persistent Cookies</h4>
                      <p className="text-sm">Remain for a set period (typically 1-24 months)</p>
                    </div>
                  </div>
                  <p>
                    We regularly review and delete unnecessary cookie data in accordance with our data retention policies.
                  </p>
                </div>
              </section>

              {/* Updates */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Updates to This Policy</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    We may update this Cookie Policy from time to time to reflect changes in our practices 
                    or applicable laws. We will notify you of any material changes by:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Posting the updated policy on our website</li>
                    <li>Updating the "Last updated" date</li>
                    <li>Sending email notifications for significant changes</li>
                    <li>Displaying a notice on our website</li>
                  </ul>
                </div>
              </section>

              {/* Contact Information */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Contact Us</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    If you have any questions about our use of cookies or this Cookie Policy, please contact us:
                  </p>
                  <div className="bg-gray-900/50 border border-gray-600 rounded-macos p-4">
                    <p><strong>RF Digital</strong></p>
                    <p>Email: privacy@rfdigi.co.za</p>
                    <p>Address: South Africa</p>
                    <p>Data Protection Officer: As required under POPIA</p>
                  </div>
                </div>
              </section>

            </div>

            {/* Back to Home */}
            <div className="mt-16 text-center">
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 text-base font-medium text-white bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos hover:from-primary-600 hover:to-orange-600 transition-all duration-300 shadow-macos"
              >
                Back to Home
              </Link>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;